import { VerifiedStatusIcon } from '@bravemove/design-tokens/icons';
import { useFormat, useI18n } from '@bravemove/i18n/react/core';
import { cn } from '../../utils/cn';
import { Tooltip } from '../Tooltip/Tooltip';
import { useVerifiedTickConfig } from '../VerifiedTickProvider/VerifiedTickProvider';
import type { VerifiableEntityType } from './entityTypes';
import { parseSince, tickHeroCriteria, tickHeroSince, tickLabel, tickTooltip } from './copy';

export type VerifiedTickSize = 'sm' | 'md' | 'lg';
export type VerifiedTickVariant = 'inline' | 'hero';

export interface VerifiedTickProps {
  /** Glyph size — 14 / 20 / 32 px (F02 "21 · Verified blue tick"). Default `md`. */
  readonly size?: VerifiedTickSize | undefined;
  /**
   * Which kind of entity is verified. Drives the a11y label + tooltip
   * (`verification:tick.label.<type>` / `tick.tooltip.<type>`) and the hero
   * criteria line. Omitted → generic "Verified".
   */
  readonly entityType?: VerifiableEntityType | undefined;
  /**
   * `inline` (default) — the glyph next to a name.
   * `hero` — the standalone profile-hero card (tinted info panel, mono title,
   * criteria subline + optional "Since …").
   */
  readonly variant?: VerifiedTickVariant | undefined;
  /** Hero only — ISO date the entity was verified; rendered as "Since <date>". */
  readonly since?: string | null | undefined;
  /**
   * Inline only. `true` (default) → when the `VerifiedTickProvider` supplies an
   * explainer href, the tick is a link (via the provider's `renderLink`) with a
   * hover/focus tooltip. `false` → an inert `role="img"` with a plain `title`.
   * Use `false` inside clickable cards (nested links are invalid).
   */
  readonly interactive?: boolean | undefined;
  /** Overrides the localized label (a11y name + `showLabel` text). Back-compat. */
  readonly label?: string | undefined;
  /**
   * Render the label text next to the glyph. Back-compat — prefer
   * `variant="hero"` for the extended profile-header treatment.
   */
  readonly showLabel?: boolean | undefined;
  readonly className?: string | undefined;
}

const SIZE: Record<VerifiedTickSize, number> = { sm: 14, md: 20, lg: 32 };
const TEXT: Record<VerifiedTickSize, string> = {
  sm: 'text-[10px]',
  md: 'text-[11px]',
  lg: 'text-[13px]',
};
const HERO_GLYPH = 20;

/**
 * Verified blue tick (F09 v2, decision D7) — ONE tick, ONE colour: the `info`
 * token (blue on all four palettes). The label/tooltip names the entity type.
 *
 * Copy comes from the shared `verification` namespace with English
 * `defaultValue`s, so the primitive renders without an i18n provider.
 */
export function VerifiedTick({
  size = 'md',
  entityType,
  variant = 'inline',
  since = null,
  interactive = true,
  label,
  showLabel = false,
  className,
}: VerifiedTickProps) {
  const { t } = useI18n();
  const { formatDate } = useFormat();
  const { explainerHref, renderLink } = useVerifiedTickConfig();

  const resolvedLabel = label ?? tickLabel(t, entityType);

  if (variant === 'hero') {
    const criteria = entityType ? tickHeroCriteria(t, entityType) : null;
    const parsed = parseSince(since);
    const sinceText = parsed
      ? tickHeroSince(t, formatDate(parsed.date, parsed.utc ? { timeZone: 'UTC' } : {}))
      : null;
    return (
      <div
        data-variant="hero"
        className={cn(
          'inline-flex items-center gap-2.5 rounded-lg border border-info/40 bg-info/10 px-3 py-2',
          className,
        )}
      >
        <span aria-hidden="true" className="inline-flex shrink-0 text-info">
          <VerifiedStatusIcon size={HERO_GLYPH} color="currentColor" />
        </span>
        <div className="min-w-0">
          <div className="font-mono text-[12px] font-bold uppercase tracking-wider text-info">
            {resolvedLabel}
          </div>
          {criteria || sinceText ? (
            <div className="mt-px text-[11px] text-ink-3">
              {criteria ? <span>{criteria}</span> : null}
              {criteria && sinceText ? <span aria-hidden="true"> · </span> : null}
              {sinceText ? <span>{sinceText}</span> : null}
            </div>
          ) : null}
        </div>
      </div>
    );
  }

  const href = interactive ? explainerHref(entityType) : null;

  const glyph = (
    <span
      role="img"
      aria-label={resolvedLabel}
      title={href ? undefined : resolvedLabel}
      data-entity-type={entityType}
      className={cn(
        'inline-flex items-center gap-1 align-middle text-info',
        href ? undefined : className,
      )}
    >
      <VerifiedStatusIcon size={SIZE[size]} color="currentColor" />
      {showLabel ? (
        <span className={cn('font-bold uppercase tracking-wider font-mono', TEXT[size])}>
          {resolvedLabel}
        </span>
      ) : null}
    </span>
  );

  if (!href) return glyph;

  const tooltip = tickTooltip(t, entityType);
  return (
    <Tooltip content={tooltip} className={cn('align-middle', className)}>
      {/* Wrapper receives the tooltip's hover/focus handlers regardless of what
          the provider's renderLink returns (focus events bubble). */}
      <span className="inline-flex">
        {renderLink({ href, ariaLabel: tooltip, children: glyph })}
      </span>
    </Tooltip>
  );
}
